import { useState } from "react";
import {
  Phone, MapPin, Clock, Star, CheckCircle2, Mail, Calendar,
  Sparkles, Home, Repeat, Building2, PackageOpen, PackageCheck,
  Hammer, Plane, Flower2, ChefHat, Bath, Wind, Settings2,
  ShieldCheck, Users, Heart, MessageSquare, Award, Timer,
} from "lucide-react";
import heroImg from "@/assets/hero-clean-home.jpg";

const PHONE = "(615) 554-4795";
const PHONE_TEL = "+16155544795";
const ADDRESS = "101 College St, Smyrna, TN 37167";
const MAPS_URL = "https://maps.google.com/?cid=6261971179722121267";

const services = [
  { icon: Home, name: "House Cleaning", desc: "Top-to-bottom cleaning that leaves every room fresh." },
  { icon: Repeat, name: "Recurring Cleaning", desc: "Stay consistently clean with scheduled visits." },
  { icon: Calendar, name: "Weekly Cleaning", desc: "Keep your home spotless week after week." },
  { icon: Calendar, name: "Bi-Weekly Cleaning", desc: "The most popular schedule for busy homes." },
  { icon: Calendar, name: "Monthly Cleaning", desc: "A reliable monthly refresh you can count on." },
  { icon: Sparkles, name: "Deep Cleaning", desc: "An intensive, detail-driven reset of your home." },
  { icon: PackageOpen, name: "Move-In Cleaning", desc: "Move into a sparkling, sanitized new home." },
  { icon: PackageCheck, name: "Move-Out Cleaning", desc: "Leave your old place spotless and ready." },
  { icon: Building2, name: "Apartment Cleaning", desc: "Trusted cleaning for apartments of any size." },
  { icon: Building2, name: "Condo Cleaning", desc: "Thoughtful, thorough condo cleaning service." },
  { icon: Home, name: "Residential Cleaning", desc: "Customized care for every type of home." },
  { icon: ChefHat, name: "Kitchen Cleaning", desc: "Degreased, sanitized, and shining kitchens." },
  { icon: Bath, name: "Bathroom Cleaning", desc: "Disinfected, scrubbed, and refreshed bathrooms." },
  { icon: Wind, name: "Dusting & Vacuuming", desc: "Detailed dusting and pet-friendly vacuuming." },
  { icon: Hammer, name: "Post-Construction Cleaning", desc: "Remove dust and debris after a renovation." },
  { icon: Plane, name: "Vacation Rental Cleaning", desc: "Turnover cleaning your guests will rave about." },
  { icon: Plane, name: "Airbnb Cleaning", desc: "Fast, reliable turnovers for short-term rentals." },
  { icon: Flower2, name: "Spring Cleaning", desc: "A seasonal refresh from baseboards to blinds." },
  { icon: Settings2, name: "Custom Cleaning Plans", desc: "A plan built around your home and priorities." },
];

const reasons = [
  { icon: Timer, title: "Reliable & On-Time", desc: "Show-up you can count on, every appointment." },
  { icon: Heart, title: "Friendly Professional Team", desc: "Courteous, background-aware cleaners you'll trust." },
  { icon: Sparkles, title: "Attention to Detail", desc: "We notice the little things that make a home shine." },
  { icon: Settings2, title: "Customized Cleaning Plans", desc: "Built around your home, schedule, and priorities." },
  { icon: Award, title: "Consistent Quality", desc: "The same spotless result every single visit." },
  { icon: MessageSquare, title: "Excellent Communication", desc: "Easy booking, clear updates, quick replies." },
  { icon: Users, title: "Locally Owned & Operated", desc: "Proudly serving Smyrna and Middle Tennessee." },
  { icon: ShieldCheck, title: "100% Satisfaction Focused", desc: "If something's off, we make it right." },
];

const testimonials = [
  { quote: "You can't do better than Made To Clean for all of your housekeeping needs! Carmen Rubio and her crew are absolutely perfect in every way. They clean your home with meticulous care and are completely dependable.", name: "Emily M. Prather" },
  { quote: "Book an appointment! You will be impressed by how communicative, organized, and professional Made To Clean is. I am so happy with their work and the team is friendly and thorough.", name: "Jesse Brown" },
  { quote: "If you're looking for a cleaning service that feels like having your home refreshed by a group of trusted friends, look no further than Made To Clean. Their friendly crew arrived on time and delivered exceptional service.", name: "Josh Baltz" },
];

const areas = [
  "Smyrna", "Murfreesboro", "La Vergne", "Nolensville", "Antioch",
  "Nashville", "Brentwood", "Franklin", "Lebanon", "Mt. Juliet",
  "Rutherford County", "Williamson County", "Davidson County",
];

export default function App() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Nav />
      <main>
        <Hero />
        <TrustBar />
        <About />
        <Services />
        <WhyUs />
        <Testimonials />
        <ServiceAreas />
        <MapSection />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

function Nav() {
  const [open, setOpen] = useState(false);
  const links = [
    ["About", "#about"], ["Services", "#services"], ["Why Us", "#why"],
    ["Reviews", "#reviews"], ["Areas", "#areas"], ["Contact", "#contact"],
  ] as const;
  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-background/85 backdrop-blur-md">
      <div className="container-prose flex h-16 items-center justify-between">
        <a href="#top" className="flex items-center gap-2 font-display text-xl font-semibold tracking-tight">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-to-br from-primary to-accent text-primary-foreground shadow-soft">
            <Sparkles className="h-4 w-4" />
          </span>
          Made To Clean
        </a>
        <nav className="hidden items-center gap-7 md:flex">
          {links.map(([l, h]) => (
            <a key={h} href={h} className="text-sm font-medium text-muted-foreground transition-colors hover:text-primary">{l}</a>
          ))}
        </nav>
        <div className="hidden md:block">
          <a href="#contact" className="inline-flex h-10 items-center rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-soft transition hover:opacity-90">
            Get Free Quote
          </a>
        </div>
        <button onClick={() => setOpen(!open)} className="md:hidden rounded-md p-2 text-foreground" aria-label="Menu">
          <div className="space-y-1.5">
            <span className="block h-0.5 w-5 bg-current" />
            <span className="block h-0.5 w-5 bg-current" />
            <span className="block h-0.5 w-5 bg-current" />
          </div>
        </button>
      </div>
      {open && (
        <div className="border-t border-border bg-background md:hidden">
          <div className="container-prose flex flex-col py-3">
            {links.map(([l, h]) => (
              <a key={h} href={h} onClick={() => setOpen(false)} className="py-2 text-sm font-medium text-foreground">{l}</a>
            ))}
            <a href="#contact" onClick={() => setOpen(false)} className="mt-2 inline-flex h-11 items-center justify-center rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground">Get Free Quote</a>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary-soft via-background to-accent-soft" />
      <div className="container-prose grid gap-12 py-16 md:grid-cols-2 md:gap-10 md:py-24 lg:py-28">
        <div className="flex flex-col justify-center">
          <span className="inline-flex w-fit items-center gap-2 rounded-full border border-border bg-background/70 px-3 py-1 text-xs font-semibold tracking-wide text-primary">
            <Star className="h-3.5 w-3.5 fill-current" /> 5.0 Google Rated · Smyrna, TN
          </span>
          <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            Professional House Cleaning Services in <span className="text-primary">Smyrna, TN</span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Reliable, thorough &amp; affordable cleaning services for homes across Smyrna and surrounding areas.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a href="#contact" className="inline-flex h-12 items-center justify-center rounded-full bg-primary px-7 text-sm font-semibold text-primary-foreground shadow-lift transition hover:translate-y-[-1px] hover:opacity-95">
              Get Free Quote
            </a>
            <a href={`tel:${PHONE_TEL}`} className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-primary bg-background px-7 text-sm font-semibold text-primary transition hover:bg-primary-soft">
              <Phone className="h-4 w-4" /> Call {PHONE}
            </a>
          </div>
          <dl className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              ["5.0★", "Google Rated"],
              ["100%", "Satisfaction"],
              ["13+", "Areas Served"],
              ["M–F", "8AM – 5PM"],
            ].map(([n, l]) => (
              <div key={l} className="rounded-xl border border-border bg-card p-3 text-center shadow-card">
                <dt className="font-display text-xl font-semibold text-primary">{n}</dt>
                <dd className="mt-0.5 text-xs text-muted-foreground">{l}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="relative">
          <div className="absolute -inset-6 -z-10 rounded-[2rem] bg-gradient-to-tr from-primary/15 to-accent/15 blur-2xl" />
          <img
            src={heroImg}
            alt="Bright, professionally cleaned living room in a Smyrna TN home"
            width={1600}
            height={1100}
            className="aspect-[4/3] w-full rounded-3xl object-cover shadow-lift"
          />
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-2xl border border-border bg-background/95 p-4 shadow-card backdrop-blur sm:left-6 sm:right-auto sm:max-w-xs">
            <div>
              <div className="flex text-accent">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              </div>
              <p className="mt-1 text-xs font-medium text-foreground">Loved by homeowners across Middle TN</p>
            </div>
            <CheckCircle2 className="h-8 w-8 text-accent" />
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const items = [
    "5-Star Rated", "Reliable Team", "Professional Cleaning", "Satisfaction Focused",
  ];
  return (
    <section aria-label="Trust badges" className="border-y border-border bg-surface">
      <div className="container-prose flex flex-wrap items-center justify-center gap-x-10 gap-y-3 py-5">
        {items.map((t) => (
          <div key={t} className="flex items-center gap-2 text-sm font-medium text-foreground">
            <CheckCircle2 className="h-4 w-4 text-accent" /> {t}
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionHead({ eyebrow, title, sub }: { eyebrow: string; title: string; sub?: string }) {
  return (
    <div className="mx-auto max-w-2xl text-center">
      <span className="inline-block rounded-full bg-accent-soft px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent">{eyebrow}</span>
      <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">{title}</h2>
      {sub && <p className="mt-4 text-base leading-relaxed text-muted-foreground">{sub}</p>}
    </div>
  );
}

function About() {
  return (
    <section id="about" className="py-20 sm:py-24">
      <div className="container-prose">
        <SectionHead eyebrow="About Us" title="A Cleaning Company You Can Trust" />
        <p className="mx-auto mt-8 max-w-3xl text-center text-lg leading-relaxed text-muted-foreground">
          Made To Clean proudly serves homeowners throughout Smyrna and Middle Tennessee with dependable,
          detail-oriented cleaning services. Our experienced cleaning professionals are known for their thoroughness,
          reliability, friendly customer service, and consistent results. We believe every client deserves a spotless,
          refreshed home — without the stress of doing it themselves.
        </p>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="services" className="bg-surface py-20 sm:py-24">
      <div className="container-prose">
        <SectionHead
          eyebrow="What We Do"
          title="Our Cleaning Services"
          sub="From recurring upkeep to deep cleans and move-in/move-out turnovers — we tailor every visit to your home."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map(({ icon: Icon, name, desc }) => (
            <article key={name} className="group flex flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition hover:-translate-y-1 hover:shadow-lift">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary-soft text-primary transition group-hover:bg-primary group-hover:text-primary-foreground">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-foreground">{name}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{desc}</p>
              <a href="#contact" className="mt-4 inline-flex items-center text-sm font-semibold text-primary hover:underline">
                Request a quote →
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyUs() {
  return (
    <section id="why" className="py-20 sm:py-24">
      <div className="container-prose">
        <SectionHead eyebrow="Why Us" title="Why Homeowners Choose Made To Clean" />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {reasons.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-accent-soft text-accent">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-base font-semibold text-foreground">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section id="reviews" className="bg-gradient-to-b from-primary-soft to-background py-20 sm:py-24">
      <div className="container-prose">
        <SectionHead eyebrow="Testimonials" title="What Our Clients Say" />
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {testimonials.map((t) => (
            <figure key={t.name} className="flex flex-col rounded-2xl border border-border bg-card p-7 shadow-card">
              <div className="flex text-accent">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              </div>
              <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground">"{t.quote}"</blockquote>
              <figcaption className="mt-5 text-sm font-semibold text-primary">— {t.name}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceAreas() {
  return (
    <section id="areas" className="py-20 sm:py-24">
      <div className="container-prose">
        <SectionHead
          eyebrow="Service Areas"
          title="Areas We Serve"
          sub="Proudly cleaning homes throughout Smyrna and across Middle Tennessee."
        />
        <ul className="mx-auto mt-10 flex max-w-4xl flex-wrap justify-center gap-2.5">
          {areas.map((a) => (
            <li key={a} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-foreground shadow-card">
              <MapPin className="h-3.5 w-3.5 text-primary" /> {a}, TN
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function MapSection() {
  return (
    <section id="map" className="bg-surface py-20 sm:py-24">
      <div className="container-prose">
        <SectionHead eyebrow="Visit" title="Find Us" />
        <div className="mt-10 grid gap-6 md:grid-cols-5">
          <div className="md:col-span-2 rounded-2xl border border-border bg-card p-7 shadow-card">
            <h3 className="font-display text-xl font-semibold text-foreground">Made To Clean</h3>
            <div className="mt-5 space-y-4 text-sm">
              <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 text-foreground hover:text-primary">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span>{ADDRESS}</span>
              </a>
              <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-3 text-foreground hover:text-primary">
                <Phone className="h-5 w-5 shrink-0 text-primary" /> {PHONE}
              </a>
              <div className="flex items-start gap-3 text-foreground">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <div>Monday – Friday: 8:00 AM – 5:00 PM</div>
                  <div className="text-muted-foreground">Saturday – Sunday: Closed</div>
                </div>
              </div>
            </div>
            <a href={`tel:${PHONE_TEL}`} className="mt-6 inline-flex h-11 items-center justify-center rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-soft hover:opacity-90">
              <Phone className="mr-2 h-4 w-4" /> Call Now
            </a>
          </div>
          <div className="md:col-span-3 overflow-hidden rounded-2xl border border-border shadow-card">
            <iframe
              title="Made To Clean on Google Maps"
              src="https://www.google.com/maps?q=101+College+St,+Smyrna,+TN+37167&output=embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="h-[380px] w-full md:h-full"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [sent, setSent] = useState(false);
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSent(true);
  };
  return (
    <section id="contact" className="py-20 sm:py-24">
      <div className="container-prose">
        <div className="overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-primary to-[oklch(0.42_0.16_240)] text-primary-foreground shadow-lift">
          <div className="grid gap-0 md:grid-cols-5">
            <div className="md:col-span-2 p-8 sm:p-10">
              <span className="inline-block rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider">Free Quote</span>
              <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Request a Free Cleaning Quote</h2>
              <p className="mt-4 text-sm leading-relaxed text-primary-foreground/85">
                Tell us about your home and the cleaning you need. We'll get back to you quickly with a tailored quote.
              </p>
              <div className="mt-8 space-y-3 text-sm">
                <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-3 hover:underline"><Phone className="h-4 w-4" /> {PHONE}</a>
                <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:underline"><MapPin className="h-4 w-4" /> {ADDRESS}</a>
                <div className="flex items-center gap-3"><Clock className="h-4 w-4" /> Mon – Fri · 8 AM – 5 PM</div>
              </div>
            </div>
            <form onSubmit={onSubmit} className="md:col-span-3 space-y-4 bg-background p-8 text-foreground sm:p-10">
              {sent ? (
                <div className="flex h-full min-h-[400px] flex-col items-center justify-center text-center">
                  <CheckCircle2 className="h-14 w-14 text-accent" />
                  <h3 className="mt-4 font-display text-2xl font-semibold">Thank you!</h3>
                  <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                    Your quote request has been received. We'll be in touch shortly. For urgent requests, please call {PHONE}.
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Name" name="name" type="text" required />
                    <Field label="Phone" name="phone" type="tel" required />
                  </div>
                  <Field label="Email" name="email" type="email" required icon={Mail} />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-foreground">Service Needed</label>
                      <select required name="service" className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20">
                        <option value="">Select a service…</option>
                        {services.map((s) => <option key={s.name}>{s.name}</option>)}
                      </select>
                    </div>
                    <Field label="Preferred Cleaning Date" name="date" type="date" icon={Calendar} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-foreground">Message</label>
                    <textarea name="message" rows={4} placeholder="Tell us about your home and what you need cleaned…"
                      className="w-full rounded-lg border border-input bg-background p-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
                  </div>
                  <button type="submit" className="inline-flex h-12 w-full items-center justify-center rounded-full bg-primary px-7 text-sm font-semibold text-primary-foreground shadow-soft transition hover:opacity-90 sm:w-auto">
                    Get My Free Quote
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type, required, icon: Icon }: {
  label: string; name: string; type: string; required?: boolean; icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div>
      <label htmlFor={name} className="mb-1.5 block text-xs font-semibold text-foreground">{label}{required && " *"}</label>
      <div className="relative">
        {Icon && <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />}
        <input id={name} name={name} type={type} required={required}
          className={`h-11 w-full rounded-lg border border-input bg-background text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 ${Icon ? "pl-10 pr-3" : "px-3"}`} />
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border bg-foreground text-background/90">
      <div className="container-prose grid gap-10 py-14 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 font-display text-xl font-semibold text-background">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-to-br from-primary to-accent">
              <Sparkles className="h-4 w-4 text-primary-foreground" />
            </span>
            Made To Clean
          </div>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-background/70">
            Professional house cleaning services in Smyrna, TN and across Middle Tennessee — reliable, thorough, and built around your home.
          </p>
        </div>
        <div>
          <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-background">Contact</h3>
          <ul className="mt-4 space-y-2.5 text-sm text-background/75">
            <li><a className="hover:text-background" href={`tel:${PHONE_TEL}`}>{PHONE}</a></li>
            <li><a className="hover:text-background" href={MAPS_URL} target="_blank" rel="noopener noreferrer">{ADDRESS}</a></li>
            <li><a className="hover:text-background" href="https://madetoclean.net" target="_blank" rel="noopener noreferrer">madetoclean.net</a></li>
          </ul>
        </div>
        <div>
          <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-background">Hours</h3>
          <ul className="mt-4 space-y-2.5 text-sm text-background/75">
            <li>Monday – Friday: 8 AM – 5 PM</li>
            <li>Saturday – Sunday: Closed</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-background/10">
        <div className="container-prose flex flex-col items-center justify-between gap-2 py-5 text-xs text-background/60 sm:flex-row">
          <span>© 2026 Made To Clean. All Rights Reserved.</span>
          <span>House Cleaning Smyrna TN · Residential Cleaning Middle Tennessee</span>
        </div>
      </div>
    </footer>
  );
}
